import { type App, inject, type Ref, ref } from 'vue'
import { usePrevioDS } from '@previo/design-system'
import * as PusherPushNotifications from '@pusher/push-notifications-web'
import { PusherBeamsClient } from '~/plugins/push-notifications/client/PusherBeamsClient'
import type { IPushNotificationClient } from '~/plugins/push-notifications/client/types/IPushNotificationClient'
import { WebPushClient } from '~/plugins/push-notifications/client/WebPushClient'

const { usePlatform } = usePrevioDS()

const isReady = ref<boolean>(false)

type PushNotificationsOptions = {
  pusherBeamsInstanceId: string
}

export const configurePushNotifications = async (
  app: App,
  options: PushNotificationsOptions,
): Promise<void> => {
  if (usePlatform().isAppleHandheldDevice() && !usePlatform().isPWA()) {
    return // not supported
  }

  if (
    (usePlatform().isAppleHandheldDevice() && usePlatform().isPWA()) ||
    usePlatform().isMacSafari()
  ) {
    app.provide('pushNotificationsClient', new WebPushClient())

    isReady.value = true
    return
  }

  if ('serviceWorker' in navigator) {
    const serviceWorkerRegistration = await navigator.serviceWorker.ready

    app.provide(
      'pushNotificationsClient',
      new PusherBeamsClient(
        new PusherPushNotifications.Client({
          instanceId: options.pusherBeamsInstanceId,
          serviceWorkerRegistration: serviceWorkerRegistration,
        }),
      ),
    )

    isReady.value = true
  }
}

type PushNotifications = {
  instance: IPushNotificationClient
  isReady: Ref<boolean>
}

export const usePushNotifications = (): PushNotifications => {
  const instance = inject<IPushNotificationClient>('pushNotificationsClient')!

  return {
    instance,
    isReady,
  }
}
